import React, { FC, useEffect, useState } from 'react'
import './general-progress-bar.css'

// UTILITIES
import { flatten } from '@utils/string-helper'


interface Props extends React.HTMLProps<HTMLDivElement> {
    ghostValue?: number
    value: number
    max: number
    settings?: GeneralProgressBarSettings
}

interface GeneralProgressBarSettings {
    paletteColor?: string
    trackColor?: string
    ghostColor?: string
    fillColor?: string
    useThumb?: boolean
}

const clamp = (min: number, value: number, max: number) => Math.max((Math.min(value, max)), min)
const getPercent = (value: number, max: number, floor = 0) => clamp(floor, ((Math.floor((value / max) * 100)) || 0), 100)
const getGhostPercent = (ghostValue: number | undefined, value: number, max: number, floor = 0) => {
    if (ghostValue != null) return getPercent(value + ghostValue, max, floor)
    else return 0
}

const GeneralProgressBar: FC<Props> = (props) => {
    const { className: classProps, ghostValue, value, max, settings, style, ...divProps } = props
    const { paletteColor, trackColor, ghostColor, fillColor, useThumb } = settings || {}
    const [percentage, setPercentage] = useState(getPercent(value, max, useThumb ? 1 : 0))
    const [ghostPercentage, setGhostPercentage] = useState(getGhostPercent(ghostValue, value, max, useThumb ? 1 : 0))

    const className = flatten({ palette: !!paletteColor }, "progressbar", classProps)

    useEffect(() => {
        const percent = getPercent(value, max, useThumb ? 1 : 0)
        const ghost = getGhostPercent(ghostValue, value, max, useThumb ? 1 : 0)

        setPercentage(percent)
        setGhostPercentage((percent !== percentage && percent === ghostPercentage) ? ghostPercentage : ghost)
    }, [value, max, useThumb, ghostValue])

    return (
        <div className={className} style={{ color: paletteColor, ...style }} {...divProps}>
            <div className="track" style={{ background: trackColor }} />
            <div className="ghost" style={{ width: `${ghostPercentage}%`, background: ghostColor || fillColor }} />
            <div className="fill" style={{ width: `${percentage}%`, background: fillColor }}>
                {useThumb && (
                    <div className="thumb">
                        <svg height="100%" viewBox="0 0 10 10" style={{ overflow: 'visible' }}>
                            <circle cx="5" cy="5" r="100%" fill={fillColor} />
                            <circle cx="5" cy="5" r="40%" fill="var(--splitcard-white)" />
                        </svg>
                    </div>
                )}
            </div>
        </div>
    )
}

export { GeneralProgressBar }